
import "./styles/main.scss";

import Router from "./Router.js";
import { PageTemplate } from "@/interfaces/PageTemplate";
import {
  computed,
  defineComponent,
  inject,
  onMounted,
  ref,
  compile,
  nextTick,
} from "vue";
import { debounce } from "lodash";

import { gsap, ScrollTrigger } from "gsap/all";

gsap.registerPlugin(ScrollTrigger);

export default defineComponent({
  name: "NMM",
  template: "#page-template",
  setup() {
    const emitter = inject("emitter");
    const menuIsOpen = ref(false);
    const sidebar = ref("");
    const newPageContentLoaded = ref(false);
    const oldPageRemoved = ref(false);
    const compiledTemplate = ref<PageTemplate | null>({
      path: "empty",
      template: compile("<div/>"),
    });
    const nextTemplate = ref<PageTemplate | null>(null);
    const componentScrollAnimations = ref<[ScrollTrigger] | null>(null);

    // eslint-disable-next-line no-unused-vars
    const router = ref(
      new Router({
        prefetch: true,
        updatePage: prepareNewPage,
      })
    );

    const windowWidth = ref(window.innerWidth);
    const windowHeight = ref(window.innerHeight);

    onMounted(() => {
      emitIsMobileState();
      window.addEventListener(
        "resize",
        debounce(() => {
          windowWidth.value = window.innerWidth;
          windowHeight.value = window.innerHeight;
          emitIsMobileState();
          emitScreenSizeChanged({
            width: window.innerWidth,
            height: window.innerHeight,
          });

          ScrollTrigger.refresh();
        }, 250)
      );

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore:next-line
      emitter.on("new-page-loaded", () => {
        newPageContentLoaded.value = true;
        nextTick(() => {
          if (oldPageRemoved.value) {
            displayNewPage();
          }
        });
      });

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore:next-line
      emitter.on("old-page-removed", () => {
        oldPageRemoved.value = true;
        nextTick(() => {
          if (newPageContentLoaded.value) {
            displayNewPage();
          }
        });
      });

      const displayNewPage = (): void => {
        if (nextTemplate.value !== null) {
          compiledTemplate.value = nextTemplate.value!;
          nextTemplate.value = null;
          newPageContentLoaded.value = false;
          oldPageRemoved.value = false;

          nextTick(() => {
            initiatePage();
          });
        }
      };

      /*
       REMOVE LOADING SCREEN
       */
      //hide UI
      gsap.set(".navigation__main_menu_label", {
        autoAlpha: 0,
      });
      gsap.set(".navigation__side_menu_item", {
        autoAlpha: 0,
      });

      if (document.querySelector(".latest_news__toggle") !== null) {
        gsap.set(".latest_news__toggle", {
          autoAlpha: 0,
        });
      }

      if (document.querySelector(".hero__scroll_indicator")) {
        gsap.set(".hero__scroll_indicator ", { autoAlpha: 0 });
      }

      gsap
        .timeline({
          delay: 1.2,
          onComplete: () => {
            document.querySelector(".loadingscreen")!.remove();
          },
        })

        .add(() => {
          document
            .querySelector(".loadingscreen__progress")!
            .classList.add("loadingscreen__progress--notransition");
        })
        .to(".loadingscreen__progress", {
          scaleX: 1,
          duration: 0.4,
          ease: "power4.out",
        })
        .to(
          ".loadingscreen__bar_container",
          {
            y: -400,
            autoAlpha: 0,
            duration: 1.4,
            ease: "power4.in",
          },
          "+=0.2"
        )
        .to(
          ".loadingscreen__logo",
          {
            y: -400,
            autoAlpha: 0,
            duration: 1.4,
            ease: "power4.in",
          },
          "-=1.3"
        )
        .add(() => {
          const initialPageContentTemplate =
            document.getElementById("page-content")?.innerHTML;

          compiledTemplate.value = {
            path: "start",
            template: compile(initialPageContentTemplate as string),
          };

          nextTick(() => {
            //init page at first mount;
            initiatePage();
          });
        })
        .to(".loadingscreen", { autoAlpha: 0, duration: 0.8 }, "-=0.8");
    });

    function prepareNewPage(path: string, templateString: string): void {
      nextTemplate.value = {
        path: path + Date.now(), // unique key
        template: compile(templateString),
      };
    }

    const isMobile = computed(() => {
      return windowWidth.value < 768;
    });

    const emitIsMobileState = (): void => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore:next-line
      emitter.emit("is-mobile-changed", isMobile.value);
    };

    const emitScreenSizeChanged = (windowSize: {
      width: number;
      height: number;
    }): void => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore:next-line
      emitter.emit("screen-size-changed", windowSize);
    };

    const toggleSidebar = (selectedSidebar: string): void => {
      if (
        (sidebar.value === "none" || sidebar.value === "") &&
        selectedSidebar === "none"
      ) {
        return;
      }

      if (selectedSidebar === sidebar.value || selectedSidebar === "none") {
        sidebar.value = "";

        if (sidebar.value === "none" && selectedSidebar === "none") {
          gsap.set(
            ".navigation--top, .navigation--corners, .pagewrapper ,.navigation__side_menu_item",
            { x: 0, clearProps: "all" }
          );
        }

        gsap.to(
          ".navigation--top, .navigation--corners, .pagewrapper ,.navigation__side_menu_item",
          { x: 0, clearProps: "all" }
        );
        menuIsOpen.value = false;
        document.body.classList.remove("lockScroll");
      } else {
        sidebar.value = selectedSidebar;

        const tl = gsap.timeline();

        if (!menuIsOpen.value) {
          const sidebar = document.querySelector(".sidebar");
          const boundingbox = sidebar!.getBoundingClientRect();

          document.body.classList.add("lockScroll");
          tl.to(
            ".navigation--top, .navigation--corners, .pagewrapper ,.navigation__side_menu_item",
            {
              x: "-=" + boundingbox.width + "px",
              ease: "power4.out",
              duration: 1,
            },
            0
          );
        }

        tl.from(
          ".sidebar_container",
          { x: "20%", autoAlpha: 0, ease: "power4.out", duration: 1 },
          0.1
        );
        menuIsOpen.value = true;
      }
    };
    const initiatePage = (): void => {
      // clean old scrolltriggers
      if (componentScrollAnimations.value !== null) {
        componentScrollAnimations.value.forEach((scrolltrigger) => {
          scrolltrigger.kill();
        });
        componentScrollAnimations.value = null;
      }

      //reset all elements where mix-blendmode was disabled
      const dirtyElements = document.querySelectorAll(".no-mix-blendmode");

      if (dirtyElements.length > 0) {
        dirtyElements.forEach((element) => {
          element.classList.remove("no-mix-blendmode");
        });
      }

      const areasThatDontWorkWithMixBlendMode =
        document.querySelectorAll<HTMLElement>(".remove-mix-blend-mode");

      if (areasThatDontWorkWithMixBlendMode.length) {
        const elementsWithMixBlendMode =
          document.querySelectorAll(".usesMixBlendMode");

        areasThatDontWorkWithMixBlendMode.forEach((area) => {
          elementsWithMixBlendMode.forEach((element) => {
            const scrolltrigger = ScrollTrigger.create({
              trigger: area,
              start: () => {
                const currentScroll =
                  document.documentElement.scrollTop || document.body.scrollTop;
                const areaRect = area.getBoundingClientRect();
                const elemRect = element.getBoundingClientRect();

                let areaTop = area.dataset.scrolltriggerStart || areaRect.top; //on e.g. HeroPost the area is initially pushed down 100%, data-scrolltriggerStart (if set) overrules calculated value

                if (typeof areaTop === "string") {
                  areaTop = parseInt(areaTop);
                }

                return areaTop - elemRect.top - elemRect.height + currentScroll;
              },
              end: () => {
                const currentScroll =
                  document.documentElement.scrollTop || document.body.scrollTop;
                const areaRect = area.getBoundingClientRect();
                const elemRect = element.getBoundingClientRect();

                let areaTop = area.dataset.scrolltriggerStart || areaRect.top; //on e.g. HeroPost the area is initially pushed down 100%, data-scrolltriggerStart (if set) overrules calculated value

                if (typeof areaTop === "string") {
                  areaTop = parseInt(areaTop);
                }

                return areaTop - elemRect.top + areaRect.height + currentScroll;
              },
              onEnter: () => {
                element.classList.add("no-mix-blendmode");
              },
              onEnterBack: () => {
                element.classList.add("no-mix-blendmode");
              },
              onLeave: () => {
                element.classList.remove("no-mix-blendmode");
              },
              onLeaveBack: () => {
                element.classList.remove("no-mix-blendmode");
              },
            });

            if (componentScrollAnimations.value === null) {
              componentScrollAnimations.value = [scrolltrigger];
            } else {
              componentScrollAnimations.value.push(scrolltrigger);
            }
          });
        });
      }

      nextTick(() => {
        ScrollTrigger.refresh(); // Eerst refresh

        const areasThatShouldHideOnFooter =
          document.querySelectorAll<HTMLElement>(".hideOnFooter");

        if (areasThatShouldHideOnFooter.length) {
          areasThatShouldHideOnFooter.forEach((area) => {
            ScrollTrigger.create({
              trigger: ".footer",
              start: () => {
                // Dynamische berekening bij elke scroll
                const rect = area.getBoundingClientRect();
                const offsetTop = window.scrollY + rect.top;
                const offsetBottom = offsetTop + rect.height;

                return `top ${offsetBottom - 30}px`;
              },
              end: () => {
                const rect = area.getBoundingClientRect();
                const offsetTop = window.scrollY + rect.top;
                const offsetBottom = offsetTop + rect.height;

                return `top ${offsetBottom - 30}px`;
              },
              markers: false,
              refreshPriority: -1, // Zorgt ervoor dat dit na andere triggers wordt berekend

              onEnter: () => {
                area.classList.add("hideOnFooter--hidden");
              },
              onEnterBack: () => {
                area.classList.remove("hideOnFooter--hidden");
              },
            });
          });
        }
      });

      /* HIDE PAGE SECTIONS AT FIRST */
      gsap.set(
        ".pagecontainer >section, .pagecontainer > .backgroundwrapper, .pagecontainer>footer",
        {
          autoAlpha: 0,
        }
      );
    };

    return { menuIsOpen, toggleSidebar, sidebar, compiledTemplate };
  },
});
